import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/api'
import { moveToPosition, EVERYONE_LIST } from '../../../../shared/clicky-text.js'
import ClickyTextForm, { SCOPE_LABELS } from './ClickyTextForm'
import { useSyncedState } from '../../hooks/useSyncedState'

/**
 * Admin > Clicky Text. Every user manages their own numbered list here. Admins
 * also get a user picker and can edit anyone's list, or the Everyone list that
 * every tester sees first. Everyone else sees the Everyone list read-only below
 * their own. Number 1 shows first on the Testing screen.
 * Plan: docs/plans/2026-10-08-clicky-text.md.
 */
const BANNER_STYLE = { background: 'var(--color-secondary-light)', border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)' }
const ERROR_STYLE = { ...BANNER_STYLE, background: 'var(--color-error-light)', border: '1px solid var(--color-error)' }

function ClickyText({ isAdmin = false, users = [], currentUserId = null }) {
  const [ownerId, setOwnerId] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // null = closed, {} = new, item = editing
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState(null)
  const [dragId, setDragId] = useState(null)
  const [everyoneItems, setEveryoneItems] = useState([])

  const query = ownerId ? `?userId=${encodeURIComponent(ownerId)}` : ''
  const owner = users.find(u => String(u.user_id) === ownerId)
  const editingEveryone = ownerId === EVERYONE_LIST

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(`/api/clicky-text${query}`)
      setItems(response.items || [])
      setError(null)
    } catch (err) {
      setError(err.message || 'Could not load clicky text')
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => { load() }, [load])

  // The Everyone list, read-only, below a personal list. Not needed while an
  // admin is editing the Everyone list itself.
  useEffect(() => {
    if (editingEveryone) return undefined
    let cancelled = false
    api.get(`/api/clicky-text?userId=${EVERYONE_LIST}`)
      .then(response => { if (!cancelled) setEveryoneItems(response.items || []) })
      .catch(err => { if (!cancelled) setError(err.message || 'Could not load the Everyone list') })
    return () => { cancelled = true }
  }, [editingEveryone])

  async function save(form) {
    setSaving(true)
    setFormError(null)
    try {
      if (editing.id) await api.patch(`/api/clicky-text/${editing.id}`, form)
      else await api.post(`/api/clicky-text${query}`, form)
      setEditing(null)
      await load()
    } catch (err) {
      setFormError(err.message || 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  async function remove(item) {
    try {
      await api.delete(`/api/clicky-text/${item.id}`)
      await load()
    } catch (err) {
      setError(err.message || 'Could not delete')
    }
  }

  // Save a new order. Show it at once, then take the server's copy.
  async function reorder(ids) {
    const byId = new Map(items.map(item => [item.id, item]))
    setItems(ids.map((id, index) => ({ ...byId.get(id), position: index + 1 })))
    try {
      const response = await api.put(`/api/clicky-text/order${query}`, { ids })
      setItems(response.items || [])
    } catch (err) {
      setError(err.message || 'Could not save the new order')
      await load()
    }
  }

  function moveTo(id, position) {
    reorder(moveToPosition(items.map(item => item.id), id, position))
  }

  function dropOn(targetIndex) {
    if (dragId !== null) moveTo(dragId, targetIndex + 1)
    setDragId(null)
  }

  return (
    <>
      <div className="admin-header">
        <div>
          <h1>Clicky Text</h1>
          <p className="text-muted">Your own wording for the Testing notes box. Number 1 shows first. Drag a row, or type a new number, to reorder.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setFormError(null); setEditing({}) }}>+ Add clicky text</button>
      </div>

      {isAdmin && (
        <UserPicker users={users} currentUserId={currentUserId} value={ownerId} onChange={setOwnerId} />
      )}
      {owner && (
        <div style={BANNER_STYLE}>You're editing {owner.name}'s clicky text. They'll see your changes the next time they open an item.</div>
      )}
      {editingEveryone && (
        <div style={BANNER_STYLE}>You're editing the Everyone list. Every tester sees it first in their notes box, above their own clicky text.</div>
      )}
      {error && <div style={ERROR_STYLE}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="loading-spinner" /></div>
      ) : (
        <div className="card card--flush">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }} aria-label="Drag" />
                <th style={{ width: 72 }}>No.</th>
                <th>Short form</th>
                <th>Type</th>
                <th>Long form</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>No clicky text yet. Click "+ Add clicky text" to make your first one.</td></tr>
              ) : items.map((item, index) => (
                <ClickyTextRow
                  key={item.id}
                  item={item}
                  count={items.length}
                  dragging={dragId === item.id}
                  onDragStart={() => setDragId(item.id)}
                  onDrop={() => dropOn(index)}
                  onMove={position => moveTo(item.id, position)}
                  onEdit={() => { setFormError(null); setEditing(item) }}
                  onDelete={() => remove(item)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!editingEveryone && everyoneItems.length > 0 && <EveryoneList items={everyoneItems} />}

      {editing && (
        <ClickyTextForm
          item={editing.id ? editing : null}
          saving={saving}
          error={formError}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
    </>
  )
}

// The Everyone list as a plain table: no drag, no numbers to edit, no buttons.
function EveryoneList({ items }) {
  return (
    <section aria-labelledby="clicky-everyone-heading" style={{ marginTop: 'var(--space-6)' }}>
      <h2 id="clicky-everyone-heading" style={{ fontSize: '1.1rem', marginBottom: 'var(--space-1)' }}>Everyone's clicky text</h2>
      <p className="text-muted" style={{ marginBottom: 'var(--space-3)' }}>Set by admins for everyone. It shows first in your notes box, above your own.</p>
      <div className="card card--flush">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 72 }}>No.</th>
              <th>Short form</th>
              <th>Type</th>
              <th>Long form</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td>{item.position}</td>
                <td><div style={{ fontWeight: 500 }}>{item.shortForm}</div></td>
                <td>
                  <span className="badge badge-neutral">{SCOPE_LABELS[item.scope]}</span>
                  {item.category && <div className="text-muted text-sm">{item.category}</div>}
                </td>
                <td><span className="text-sm">{item.longForm}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function UserPicker({ users, currentUserId, value, onChange }) {
  const others = users.filter(u => u.is_active !== false && String(u.user_id) !== String(currentUserId))
  return (
    <div className="form-group" style={{ maxWidth: 320 }}>
      <label className="filter-label" htmlFor="clicky-user">Editing list for</label>
      <select id="clicky-user" className="form-input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Me</option>
        <option value={EVERYONE_LIST}>Everyone (all users)</option>
        {others.map(u => <option key={u.user_id} value={String(u.user_id)}>{u.name}</option>)}
      </select>
    </div>
  )
}

function ClickyTextRow({ item, count, dragging, onDragStart, onDrop, onMove, onEdit, onDelete }) {
  const [confirming, setConfirming] = useState(false)
  const [position, setPosition] = useSyncedState(String(item.position))

  function commitPosition() {
    const wanted = parseInt(position, 10)
    if (Number.isInteger(wanted) && wanted !== item.position) onMove(wanted)
    else setPosition(String(item.position))
  }

  return (
    <tr
      draggable
      onDragStart={onDragStart}
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); onDrop() }}
      style={dragging ? { opacity: 0.5 } : undefined}
    >
      <td style={{ cursor: 'grab', color: 'var(--color-text-muted)' }} title="Drag to reorder" aria-hidden="true">⋮⋮</td>
      <td>
        <input
          type="number"
          className="form-input"
          min={1}
          max={count}
          value={position}
          onChange={e => setPosition(e.target.value)}
          onBlur={commitPosition}
          onKeyDown={e => { if (e.key === 'Enter') commitPosition() }}
          aria-label={`Position of ${item.shortForm}`}
          style={{ width: 56, padding: '4px 6px', textAlign: 'center' }}
        />
      </td>
      <td><div style={{ fontWeight: 500 }}>{item.shortForm}</div></td>
      <td>
        <span className="badge badge-neutral">{SCOPE_LABELS[item.scope]}</span>
        {item.category && <div className="text-muted text-sm">{item.category}</div>}
      </td>
      <td><span className="text-sm" title={item.longForm} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.longForm}</span></td>
      <td>
        <div className="action-buttons">
          <button className="btn btn-sm btn-ghost" onClick={onEdit}>Edit</button>
          {confirming ? (
            <>
              <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>Keep</button>
            </>
          ) : (
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(true)}>Delete</button>
          )}
        </div>
      </td>
    </tr>
  )
}

export default ClickyText
